import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import styled from 'styled-components';
import { lgPress, lgSunken, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import Pagination from '../components/Pagination';
import GlassSelect from '../components/GlassSelect';
import { TableSkeleton } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { useSearchHighlight } from '../hooks/useSearchHighlight'; // [SEARCH-HIGHLIGHT 2026-09-29]
import { fmtDayText } from '../utils/dates'; // [DATE-STYLE 2026-09-29]
import SingleDateCalendar from '../components/SingleDateCalendar'; // [DATE-STYLE 2026-09-29]
import { alertDialog } from '../components/dialogService';
import { fetchActivityLogs, deleteActivityLog, ActivityLog, ActivityLogsResponse } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [ACCEPT-REFRESH 2026-09-17, user]
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE-ALL 2026-09-29] keys the remembered column widths
import { useIsMobile } from '../hooks/useIsMobile'; // [COL-RESIZE-ALL 2026-09-29] no resizing on phones
import { useColumnResize, ResizeHandle, CutCell, CutLabel, cutCellStyle } from '../components/ColumnResize'; // [COL-RESIZE-ALL 2026-09-29]
import { useRowsLand } from '../components/Fluid'; // [SMOOTH-DATES 2026-10-12] [FLUID-5 2026-10-12]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26]



/* [PERF 2026-08-14] Built ONCE - `toLocaleDateString(locale, {options})` constructs a fresh
 * Intl.DateTimeFormat per call (measured 90us on V8 vs 1.5us cached). Output identical. */
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
// IST, not UTC - these two render the audit timestamp in Asia/Kolkata. Named explicitly so
// nobody reads them as UTC formatters and "fixes" the timezone away.
const MONTH_SHORT_DAY_YEAR_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' });
const TIME_IST_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit', hour12: true });

/* ─────────────────────────── layout ─────────────────────────── */

const Page = styled.div`
  background: ${(p) => p.theme.surface};
  min-height: 100%;
  /* Bottom padding clears the floating dock so the last row sits above the pill
     instead of hiding behind it. */
  padding: 20px 24px 88px;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    /* [MOBILE 2026-07-07] Keep the full 88px dock-clearance strip so the
       pagination row at the end of the table clears the floating bottom dock.
       Was 32px, which let the last part hide behind the pill (matches the other
       tabs' padding-bottom:88px convention). */
    padding: 16px 12px 88px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  /* [PHONE-LAYOUT 2026-10-24, user] Phones: the page head centred like every other tab (title, then its line). */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
  letter-spacing: -0.2px;
`;

const Sub = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${(p) => p.theme.muted};
  max-width: 720px;
`;

/* soft glass strip — per design-liquid-glass "soft glass strip" recipe */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.35) 100%)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};

  /* [MOBILE 2026-07-07] Two-up grid on phones: Search + "All people" on the first
     row, From + To on the second — instead of each control stacking full-width. */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 10px;
    /* [PHONE-LAYOUT 2026-10-22] From and To get a full row each: the date box ("Oct 22, 2026" needs about 145 px) plus its
       slide-open clear button did not fit half a 360-390 px row, so the arrow ran over the label. */
    & > label:nth-of-type(n + 2) { grid-column: 1 / -1; }
    /* [PHONE-LAYOUT 2026-10-24, user] ...and the search and "All people" too: in half a row the search hint read
       "Search by pers…". One full row each: search, people, From, To. */
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;

  /* [MOBILE 2026-07-07] Drop the 220px floor so it fits inside the 1fr grid cell
     (left column of the first row) instead of forcing a full-width wrap. */
  @media (max-width: 768px) {
    min-width: 0;
  }

  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: ${(p) => p.theme.muted};
  }
`;

const Search = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px 0 34px;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)')};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  font-size: 13px;
  font-weight: 500;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder { color: ${(p) => p.theme.dim}; } /* [FULL-INK 2026-09-01] stays grey on purpose */
  &:focus {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
  border-radius: 999px;
`;

const FieldWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;

  /* [MOBILE 2026-07-07] Fill the grid cell and let the dropdown / date field grow
     to the cell width (icon or "From"/"To" label stays natural on the left). */
  @media (max-width: 768px) {
    display: flex;
    min-width: 0;

    button,
    input {
      flex: 1;
      min-width: 0;
    }
  }
`;

/* [DATE-STYLE 2026-09-29, user] The From / To fields are now the app's own calendar (SingleDateCalendar,
   same 38px / 10px-corner box the old browser date input had). This small × clears one. */
/* [SMOOTH-DATES 2026-10-12, user] Always rendered, and it SLIDES open when a date is picked (the next field glides
   along) instead of popping in and shoving the To field 31px right in one frame. Hidden = no width, the field's 7px
   gap cancelled, invisible (after the slide) and out of the tab order. */
const ClearDate = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $show: boolean }>`
  width: ${(p) => (p.$show ? '24px' : '0px')};
  height: 24px;
  margin-left: ${(p) => (p.$show ? '0px' : '-7px')};
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  opacity: ${(p) => (p.$show ? 1 : 0)};
  visibility: ${(p) => (p.$show ? 'visible' : 'hidden')};
  transition: width 200ms cubic-bezier(0.32, 0.72, 0, 1), margin-left 200ms cubic-bezier(0.32, 0.72, 0, 1),
    opacity 180ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${(p) => (p.$show ? '0s' : '200ms')},
    background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover { background: ${(p) => p.theme.pill}; }
  /* FieldWrap's phone rule stretches every button in the field (flex: 1); the × keeps its size. */
  && { flex: none; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
`;

const ChipsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;

  /* [MOBILE 2026-07-07] Section + status chip filters removed on phones per the
     mobile design — the toolbar's search / person / date filters remain. */
  @media (max-width: 768px) {
    display: none;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

/* [SMOOTH-DATES 2026-10-12, user] The "Clear" chip slides in / out (the status chips beside it glide) instead of
   popping in the moment the first filter or date is set. The slot clips it; hidden = no width, the row's 6px gap
   cancelled, invisible after the slide. It opens by the chip's MEASURED width ($w): with a roomier max-width the
   visible part of the slide was over in 2-3 frames — measured as a near-jump. */
const ChipSlot = styled.span<{ $show: boolean; $w: number }>`
  display: inline-flex;
  overflow: hidden;
  max-width: ${(p) => (p.$show ? `${p.$w || 140}px` : '0px')};
  margin-left: ${(p) => (p.$show ? '0px' : '-6px')};
  opacity: ${(p) => (p.$show ? 1 : 0)};
  visibility: ${(p) => (p.$show ? 'visible' : 'hidden')};
  transition: max-width 240ms cubic-bezier(0.32, 0.72, 0, 1), margin-left 240ms cubic-bezier(0.32, 0.72, 0, 1),
    opacity 180ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${(p) => (p.$show ? '0s' : '240ms')};
  & > button { flex: none; white-space: nowrap; }
`;

const Chip = styled.button<{ $active: boolean; $danger?: boolean }>`
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid
    ${(p) =>
      p.$active
        ? p.$danger
          ? p.theme.danger
          : p.theme.green600
        : p.theme.colorScheme === 'dark'
        ? 'rgba(255,255,255,0.14)'
        : 'rgba(0,0,0,0.10)'};
  background: ${(p) =>
    p.$active
      ? p.$danger
        ? p.theme.colorScheme === 'dark'
          ? 'rgba(239,68,68,0.18)'
          : 'rgba(220,38,38,0.10)'
        : p.theme.colorScheme === 'dark'
        ? 'rgba(70,191,130,0.18)'
        : 'rgba(26,159,125,0.12)'
      : 'transparent'};
  color: ${(p) =>
    p.$active
      ? p.$danger
        ? p.theme.danger
        : p.theme.colorScheme === 'dark'
        ? '#4acd8b'
        : p.theme.green600
      : p.theme.muted};
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover { color: ${(p) => (p.$danger ? p.theme.danger : p.theme.greenInk)}; } /* [GREEN-ON-DARK 2026-08-27] */
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;

/* ─────────────────────────── table ─────────────────────────── */

const Card = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 14px;
  overflow: hidden;
  background: ${(p) => p.theme.surface};
  /* [SMOOTH-DATES 2026-10-12, user] After a date / filter / page change the new rows rise in as they land instead of
     snapping in. [FLUID-5 2026-10-12, user] Every row on screen, top to bottom, as one wave (the page's useRowsLand
     on this card — components/Fluid.tsx landRows), and on the first load (a tab visit) too. */
`;

/* [SMOOTH-DATES 2026-10-12] The generic TableSkeleton's header and row heights (Skeleton.tsx: 10px / 8px padding,
   one 12px bar), used to fill a held height with the right number of skeleton rows. Card borders: 2px. */
const SK_HEAD_H = 36;
const SK_ROW_H = 29;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $center?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${(p) => p.theme.pill};
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  white-space: nowrap;
  text-align: ${(p) => (p.$center ? 'center' : 'left')};
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

const Tr = styled.tr`
  & > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  /* Row-hover tint gated by html.pointer-active (toggled in utils/hoverGate.ts)
     + hover:hover so it can never stick on a row after scrolling/reload and so
     touch devices never get a sticky highlight. */
  @media (hover: hover) {
    html.pointer-active &:hover td {
      background: ${(p) => (p.theme.colorScheme === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)')};
    }
  }
`;

const Td = styled.td<{ $center?: boolean; $top?: boolean }>`
  padding: 10px 14px;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => (p.$center ? 'center' : 'left')};
  vertical-align: ${(p) => (p.$top ? 'top' : 'middle')};
`;

/* [COL-RESIZE-ALL 2026-09-29, user] Excel-style drag-to-resize, same as Payments, for the seven data
   columns (Delete stays fixed). Only a column someone DRAGS narrower is cut (user's choice: cut
   with an ellipsis, like Payments); every untouched column keeps showing everything in full, as
   [LOG-NOTE 2026-09-18] requires. No hover titles here: [NO TOOLTIPS 2026-09-09, user]. Th and Td
   both pad 14px a side. */
const LOG_COLS: { key: string; label: string; center?: boolean }[] = [
  { key: 'time', label: 'Time changed' },
  { key: 'name', label: 'Name' },
  { key: 'section', label: 'Section', center: true },
  { key: 'action', label: 'Action' },
  { key: 'changes', label: 'Changes Made' },
  { key: 'note', label: 'Note' },
  { key: 'status', label: 'Status', center: true },
];
const LOG_COL_KEYS = LOG_COLS.map((c) => c.key);
const LOG_PAD_X = 28;

const WhenCell = styled.div`
  white-space: nowrap;
  .date { font-weight: 600; font-size: 12.5px; color: ${(p) => p.theme.text}; line-height: 1.3; }
  .time { font-size: 11.5px; color: ${(p) => p.theme.muted}; line-height: 1.3; }
`;

const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
`;

const PersonName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.theme.text};
  line-height: 1.2;
  white-space: nowrap;
`;

const PersonEmail = styled.div`
  font-size: 11.5px;
  color: ${(p) => p.theme.muted};
  line-height: 1.3;
  white-space: nowrap;
`;

const SectionChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  white-space: nowrap;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.14)' : 'rgba(26,159,125,0.10)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.28)' : 'rgba(26,159,125,0.22)')};
`;

const ActionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 160px;
`;

const ActionText = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.theme.text};
`;

// Muted caption shown under a "System · Auto Column" row's action AND in its
// changes cell, so an automatic recalculation is never mistaken for a manual
// mapping decision. Shown only for rows the auto-audit trigger wrote.
const AutoNote = styled.div`
  font-size: 11px;
  font-weight: 500;
  font-style: italic;
  color: ${(p) => p.theme.muted};
  max-width: 420px;
`;

/* [LOG-NOTE 2026-09-18, user] Widened from 420px: the cell now carries the record AND the
   values that moved, and nothing in it is allowed to be cut. $sep separates the second
   group (what changed) from the first (which record it happened to). */
const Details = styled.div<{ $sep?: boolean }>`
  max-width: 520px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  ${(p) => (p.$sep ? `margin-top: 6px; padding-top: 6px; border-top: 1px dashed ${p.theme.line};` : '')}
`;

const KV = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11.5px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')};

  b { color: ${(p) => p.theme.muted}; font-weight: 600; }
  span {
    color: ${(p) => p.theme.text};
    font-weight: 600;
    /* [LOG-NOTE 2026-09-18, user] The max-width 220px + nowrap + ellipsis that used to sit
       here is what cut 2,830 of 13,591 rows. Long values wrap instead, and anywhere lets a
       long URL or id break rather than stretch the column. */
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

/* The plain-English sentence. Shown in full, always — no clamp and no "more" toggle
   ([LOG-NOTE 2026-09-18, user] "dont show more thing, show the complete lines"), and no
   tooltip either, which this page has carried none of since 2026-09-09. Most sentences
   run to two lines; the writers' own longest note is 226 characters. */
const NoteCell = styled.div`
  max-width: 400px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${(p) => p.theme.text};
  overflow-wrap: anywhere;
`;

const Dash = styled.span`
  color: ${(p) => p.theme.muted};
`;

const StatusBadge = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.$ok ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600) : p.theme.danger)};
  background: ${(p) =>
    p.$ok
      ? p.theme.colorScheme === 'dark'
        ? 'rgba(70,191,130,0.14)'
        : 'rgba(26,159,125,0.10)'
      : p.theme.colorScheme === 'dark'
      ? 'rgba(239,68,68,0.16)'
      : 'rgba(220,38,38,0.10)'};
  border: 1px solid
    ${(p) =>
      p.$ok
        ? p.theme.colorScheme === 'dark'
          ? 'rgba(70,191,130,0.28)'
          : 'rgba(26,159,125,0.22)'
        : p.theme.colorScheme === 'dark'
        ? 'rgba(239,68,68,0.32)'
        : 'rgba(220,38,38,0.25)'};
`;

const StateNote = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

/* Premium per-row delete: a quiet ghost icon button that lights up red on hover. */
const DeleteBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  width: 30px;
  height: 30px;
  display: inline-grid;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
  cursor: pointer;
  transition: color 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => p.theme.danger};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(239,68,68,0.15)' : 'rgba(220,38,38,0.08)')};
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(239,68,68,0.32)' : 'rgba(220,38,38,0.20)')};
  }
  &:active { transform: scale(0.92); }
  &:disabled { opacity: 0.45; cursor: default; transform: none; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
`;

/* ─────────────────────────── helpers ─────────────────────────── */

/** The server stores + returns timestamps in UTC ("YYYY-MM-DDTHH:mm:ss", no
 *  zone). Parse as UTC and render in IST (Asia/Kolkata) — the team's timezone —
 *  so "09:25 UTC" reads as "2:55 PM IST". */
function fmtWhen(s: string | null): { date: string; time: string } {
  if (!s) return { date: '—', time: '' };
  const utc = new Date(`${s}Z`); // append Z so the wall-clock is read as UTC
  if (isNaN(utc.getTime())) return { date: s, time: '' };
  const date = MONTH_SHORT_DAY_YEAR_IST_FMT.format(utc);
  const time = `${TIME_IST_FMT.format(utc)} IST`;
  return { date, time };
}

function initialOf(name: string | null, email: string | null): string {
  return (name?.trim()[0] || email?.trim()[0] || '?').toUpperCase();
}

function displayValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  // [DATE-STYLE 2026-09-29, user] A logged date ("2026-09-29") reads "Sep 29, 2026", like every other
  // date on screen. Display only — the stored log is untouched.
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDayText(v);
  return String(v);
}

/** A value the user didn't touch — empty/cleared columns we don't want to show. */
function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

/* Friendly labels mirroring the Edit-Mapping modal, so a logged column reads as
 * "Performance · Recurring Upsell" instead of the raw `performance_r_so`. */
const DEPT_LABELS: Record<string, string> = {
  ranking: 'Ranking',
  performance: 'Performance',
  tech: 'Tech',
  creative: 'Creative',
};
const SUFFIX_LABELS: Record<string, string> = {
  retention: 'Retention',
  r_new_sale: 'Recurring New Sale',
  o_new_sale: 'One-Time New Sale',
  r_so: 'Recurring Upsell',
  o_so: 'One-Time Upsell',
  pia_amount: 'PIA Amount',
  pia_month: 'PIA Month',
  pia_dept: 'PIA Dept',
};
const FIELD_LABELS: Record<string, string> = {
  website: 'Website',
  email: 'Email',
  team: 'Team',
  recovery: 'Recovery',
  row_status: 'ROW Status',
  pm_name: 'PM Name',
  pm: 'PM',
  company_record_id: 'Company Record Id',
  assignee_name: 'Sales/PM Name',
  at_risk_expected_payment: 'At-Risk Expected Payment',
  // [AM-NAMES 2026-09-04, user] Explicit: prettyKey's snake_case fallback would title-case
  // this to "Am Names", and AM is an initialism. Several names arrive in one value, joined
  // by ', ' — so a change reads "Ajay Kumar -> Ajay Kumar, Manoj Yadav" with no extra work.
  am_names: 'AM Names',
  // Inventory / Payments
  amount: 'Amount', month: 'Month', status: 'Status', discount: 'Discount',
  type: 'Type', brand: 'Brand', num_campaigns: '# Campaigns', billing_date: 'Bill Day',
  payment_type: 'Payment Type', renewal_status: 'Renewal Status',
  renewal_date: 'Renewal Date', renewal_amount: 'Renewal Amount',
  subscription_status: 'Subscription Status',
  // EOM adjustment
  adjustment: 'Adjustment',
  // User Management
  is_admin: 'Admin Access', visible_to_owner: 'Visible To Team',
  allowed_sections: 'Section Access', name: 'Name',
  // Sync / system creates
  amount_paid: 'Amount', account_name: 'Account', charge_id: 'Charge Id',
  refund_amount: 'Refund Amount', dispute_amount: 'Dispute Amount',
  dispute_status: 'Dispute Status', client_name: 'Client', payment_date: 'Payment Date',
  // [LOG-COVERAGE 2026-09-17, user] Keys the newer entries carry. Explicit where the snake_case
  // fallback would read wrong ("Ranking · retention_locked", "Pi Id").
  changed_by: 'Changed By', note: 'Note', payment_ids: 'Payments', placed_value: 'Placed Value',
  refund_id: 'Refund Id', dispute_id: 'Dispute Id', refund_date: 'Refund Date', dispute_date: 'Dispute Date',
  ranking_retention_locked: 'Retention Locked', website_locked: 'Website Locked', email_locked: 'Email Locked',
  deals_record_id: 'HubSpot Deal Id', deal_owner: 'Deal Owner', pi_id: 'Payment Intent Id',
  moved_amount: 'Moved Amount', merged_into_row: 'Merged Into Row', merged_row_amount: 'Merged Row Amount',
  deleted_item: 'Deleted Item', period: 'Period',
  elite_monthly_in_bank: 'Elite Monthly In-Bank', elite_conversion: 'Elite Conversion %',
};

/** Raw column key → human label shown in the mapping screen. */
function prettyKey(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  for (const dept of Object.keys(DEPT_LABELS)) {
    if (key.startsWith(`${dept}_`)) {
      const suffix = key.slice(dept.length + 1);
      return `${DEPT_LABELS[dept]} · ${SUFFIX_LABELS[suffix] || suffix}`;
    }
  }
  // Fallback for any other section's fields: turn snake_case into Title Case.
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ───────── [LOG-NOTE 2026-09-18, user] "Note" column + uncut "Changes Made" ─────────
 * Before: every `details` key rendered as one chip whose value was capped at 220px and
 * ellipsised, so 2,830 of the table's 13,591 rows (20.8%) showed cut text — worst the
 * `deals` blob (raw JSON, up to 705 chars) and the written explanations (`note` up to
 * 226) — and those sentences sat in the same cell as the data. Nothing is cut when a row
 * is STORED (server MAX_JSON is 4,000 and the largest row on record is 966), so the full
 * value can always be shown. Now: "Changes Made" prints the record the change landed on
 * plus every value that moved, in full and copyable; "Note" carries the English sentence. */

/** Record keys pinned to the front, so the row is identifiable at a glance and the copied
 *  text always starts with the website / email / month. */
const RECORD_ORDER = ['website', 'email', 'month', 'paymentId', 'itemId', 'client_name',
  'account_name', 'charge_id', 'refund_id', 'dispute_id', 'payment_date', 'refund_date', 'dispute_date'];

/** Both arrow characters the writers use: "old → new" everywhere, and "old ⇄ new" for a
 *  website that keeps flip-flopping (sync `queries.js`). A value carrying one is a CHANGE,
 *  anything else identifies the record. Judged per VALUE, never per key — `website`
 *  identifies the row in ~11,000 entries but is itself the change in 452 of them. */
const ARROW_RE = /\s(?:→|⇄)\s/;

/** Keys that hold a written explanation rather than data. */
const PROSE_KEYS = new Set(['note', 'reason', 'description']);

/** Two writers break "the key decides", so prose is detected per value:
 *  - `/api/deleted/note` stores the deletion note's own "old → new" diff under `note`;
 *  - "Payment arrived from another program" explains a blanked website inside `website`
 *    ("blank - the guard removed what it sent…") — a real website never has a space. */
function isProseValue(key: string, value: string): boolean {
  if (ARROW_RE.test(value)) return false;
  if (PROSE_KEYS.has(key)) return true;
  return key === 'website' && /\s/.test(value);
}

/** Format a stored month value ("2026-04" / "2026-04-01") as a friendly "April 2026".
 *  Passes through anything that isn't a YYYY-MM(-DD) string; handles "old → new"
 *  transitions by formatting each side. */
function formatMonthLabel(v: string): string {
  const one = (s: string): string => {
    const m = s.trim().match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
    if (!m) return s.trim();
    return MONTH_LONG_YEAR_FMT.format(new Date(Number(m[1]), Number(m[2]) - 1, 1));
  };
  return v.includes('→') ? v.split('→').map((p) => one(p)).join(' → ') : one(v);
}

/** A list value read out flat ("Payments, EOM") instead of as the JSON it is stored as.
 *  Objects keep their JSON — the only array of objects is `deals`, handled separately. */
function displayList(values: any[]): string {
  return values.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
}

type Pair = { k: string; v: string };
/** A deal's field rendered for reading — it carries its own label, because a deal's keys
 *  are camelCase payload names ("dealName", "ownerName"), not the table's column names. */
type Line = { label: string; v: string };
/** What the two columns are built from: the record the change landed on, the values that
 *  moved, the sentence the writer stored, and any HubSpot deals sent with the request. */
type LogParts = { record: Pair[]; changes: Pair[]; prose: string[]; deals: Record<string, any>[] };

/** Flatten the details object into those four buckets. Only columns that actually carry a
 *  value are shown — unchanged columns (null/empty) are dropped so a mapping change reads
 *  as just the column(s) that were set, not a wall of `null`s. */
function splitDetails(details: Record<string, any> | null): LogParts {
  const parts: LogParts = { record: [], changes: [], prose: [], deals: [] };
  if (!details || typeof details !== 'object') return parts;
  // Whole-object overflow guard from the server's `sanitizeDetails`. It has never fired on
  // this table, but if it ever does the preview is all there is — show it rather than
  // nothing, and in full (it used to be cut to 200 chars on top of the server's own cut).
  if ((details as any)._truncated) {
    parts.record.push({ k: 'data', v: String((details as any).preview ?? '') });
    return parts;
  }
  for (const [k, raw] of Object.entries(details)) {
    if (isBlank(raw)) continue;
    if (k === 'deals' && Array.isArray(raw)) {
      parts.deals = raw.filter((d) => d && typeof d === 'object');
      continue;
    }
    const v =
      // Plain arrays (`allowed_sections` when no before-snapshot existed, the bulk-edit
      // `ids` list) used to print as raw JSON — ["my-inventory"] — on 73 rows.
      Array.isArray(raw) ? displayList(raw)
      // [BULK-EDIT] `field` carries a raw COLUMN NAME as its value ("am_names", "pm"), not
      // data — so it reads as the column's label like every other key does. Without this a
      // bulk edit logged "Field: am_names", which is the one entry in the whole log that
      // showed a database identifier to the reader.
      : k === 'field' ? prettyKey(displayValue(raw))
        : k === 'month' || k.endsWith('_month') ? formatMonthLabel(displayValue(raw))
        : displayValue(raw);
    if (isProseValue(k, v)) parts.prose.push(v);
    else if (ARROW_RE.test(v)) parts.changes.push({ k, v });
    else parts.record.push({ k, v });
  }
  // Surface website/email/month first so you can tell which record was changed.
  parts.record.sort((a, b) => {
    const ai = RECORD_ORDER.indexOf(a.k);
    const bi = RECORD_ORDER.indexOf(b.k);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return parts;
}

/* ── the `deals` payload ──
 * POST /api/hubspot/deals logs the deals it sent as an array of objects, which the cell
 * used to print as raw JSON (up to 705 chars, cut at the first ~34). A deal's amount is
 * the sum of its four department amounts, matching how routes/hubspot.ts builds it. */
const DEAL_DEPTS = ['ranking', 'creative', 'performance', 'tech'] as const;
const DEAL_FIELDS: { k: string; label: string }[] = [
  { k: 'dealName', label: 'Deal Name' },
  { k: 'pipeline', label: 'Pipeline' },
  { k: 'ownerName', label: 'Owner' },
  { k: 'paymentType', label: 'Payment Type' },
  { k: 'companyUrl', label: 'Company URL' },
  { k: 'email', label: 'Email' },
  { k: 'piaMonth', label: 'PIA Month' },
  { k: 'secondVoice', label: 'Second Voice' },
  { k: 'closeDate', label: 'Close Date' },
  { k: 'existingDealId', label: 'Existing Deal Id' },
];

const AMOUNT_FMT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function dealAmount(d: Record<string, any>): number {
  return DEAL_DEPTS.reduce((sum, k) => sum + (Number(d[k]) || 0), 0);
}

function fmtDealDate(v: unknown): string {
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? displayValue(v) : MONTH_SHORT_DAY_YEAR_IST_FMT.format(d);
}

/** One deal object → the readable lines that replace its raw JSON. */
function dealLines(d: Record<string, any>, index: number, total: number): Line[] {
  const lines: Line[] = [];
  if (total > 1) lines.push({ label: 'Deal', v: `${index + 1} of ${total}` });
  // `slot` names the mapping cell the deal was raised for ("o_so", "pia_amount") — the
  // same suffixes the Edit-Mapping modal shows, so reuse its labels.
  if (!isBlank(d.slot)) lines.push({ label: 'Mapped As', v: SUFFIX_LABELS[String(d.slot)] || String(d.slot) });
  for (const f of DEAL_FIELDS) {
    if (isBlank(d[f.k])) continue;
    lines.push({ label: f.label, v: f.k === 'closeDate' ? fmtDealDate(d[f.k]) : displayValue(d[f.k]) });
  }
  const amount = dealAmount(d);
  if (amount) lines.push({ label: 'Deal Amount', v: AMOUNT_FMT.format(amount) });
  for (const dept of DEAL_DEPTS) {
    const n = Number(d[dept]) || 0;
    if (n) lines.push({ label: DEPT_LABELS[dept], v: AMOUNT_FMT.format(n) });
  }
  return lines;
}

/* ── the Note sentence ── */

type NoteCtx = { log: ActivityLog; where: string; changes: string };

/** "on andrewmoor.co.uk for September 2026" — the record a sentence is about. */
function whereClause(parts: LogParts): string {
  const get = (k: string) => parts.record.find((p) => p.k === k)?.v || '';
  const who = get('website') || get('email') || get('client_name');
  const month = get('month');
  return [who ? `on ${who}` : '', month ? `for ${month}` : ''].filter(Boolean).join(' ');
}

/** The names of what moved — never the values. The old → new values sit in the very next
 *  cell, so repeating them here only pushed the sentence past its two lines (it did on
 *  40.7% of rows when this named them). Same rule as the Performance Review copy: a value
 *  that already has its own place on screen doesn't get a sentence too. */
function changeSummary(parts: LogParts): string {
  const names = parts.changes.map((c) => prettyKey(c.k));
  if (!names.length) return '';
  if (names.length > 3) return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function uniqStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter((v) => v && v.trim()))).slice(0, 3);
}

function dealsNote(log: ActivityLog, parts: LogParts): string {
  const deals = parts.deals;
  const n = deals.length;
  // A deal carrying `existingDealId` was updated in HubSpot rather than created.
  const updated = deals.filter((d) => !isBlank(d.existingDealId)).length;
  const verb = log.action.startsWith('Deleted') ? 'Deleted'
    : updated === n ? 'Updated'
      : updated ? 'Created and updated' : 'Created';
  const slots = uniqStrings(deals.map((d) => (isBlank(d.slot) ? '' : SUFFIX_LABELS[String(d.slot)] || String(d.slot))));
  const pipelines = uniqStrings(deals.map((d) => displayValue(d.pipeline)));
  const owners = uniqStrings(deals.map((d) => displayValue(d.ownerName)));
  const total = deals.reduce((sum, d) => sum + dealAmount(d), 0);
  const where = whereClause(parts);
  return [
    `${verb} ${n} HubSpot deal${n === 1 ? '' : 's'}`,
    slots.length ? ` (${slots.join(', ')})` : '',
    total ? ` worth ${AMOUNT_FMT.format(total)}` : '',
    pipelines.length ? ` in ${pipelines.join(' and ')}` : '',
    where ? ` ${where}` : '',
    owners.length ? `, owner ${owners.join(', ')}` : '',
    '.',
  ].join('');
}

/** Whether this row really came from the Stripe/PayPal sync. Checked on the row's own path
 *  rather than assumed from the action, because the same action strings are also produced
 *  by a person editing a payment, refund or dispute through the app. */
function fromSync(c: NoteCtx): boolean {
  return c.log.method === 'SYSTEM' && (c.log.path || '').includes('stripe-paypal-sync');
}

/** The fallback sentence: what moved, on which record. It carries the `summariseChanges`
 *  family ("Updated PM, Status", "Updated 7 fields"), where the values hold the meaning. */
function genericNote(c: NoteCtx): string {
  const bySystem = c.log.method === 'SYSTEM' || c.log.method === 'AUTO';
  if (c.changes) {
    return `${bySystem ? 'The system changed' : 'Changed'} ${c.changes}${c.where ? ` ${c.where}` : ''}.`;
  }
  const action = c.log.action ? c.log.action.charAt(0).toUpperCase() + c.log.action.slice(1) : 'Change recorded';
  return `${action}${c.where ? ` ${c.where}` : ''}.`;
}

/** Sentences for the actions whose own label says too little on its own. Everything not
 *  listed falls through to `genericNote`, so the column is never blank: measured on the
 *  live table, 132 distinct actions collapse to 89 families and the 30 biggest cover
 *  94.2% of all rows, with 25.1% already carrying a written sentence that wins outright. */
const NOTE_BY_ACTION: Record<string, (c: NoteCtx) => string> = {
  'Auto-recalculated totals': (c) =>
    `Totals recalculated automatically after the mapping or amounts changed${c.changes ? ` — ${c.changes}` : ''}.`,
  'Auto-updated derived columns': (c) =>
    `Derived columns recalculated automatically${c.changes ? ` — ${c.changes}` : ''}.`,
  'Mapped payment': (c) => `The payment's mapping was saved${c.where ? ` ${c.where}` : ''}${c.changes ? ` — ${c.changes}` : ''}.`,
  // ⚠ These six action strings are written BOTH by the sync and by a person editing the
  // row, so the sentence may only name the sync when the row's own path says so — all 3
  // "Updated dispute" entries in the table are human PUTs, not the sync.
  'Added payment': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync brought in a new payment' : 'A new payment was added'}${c.where ? ` ${c.where}` : ''}.`,
  'Added refund': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync brought in a new refund' : 'A new refund was added'}${c.where ? ` ${c.where}` : ''}.`,
  'Added dispute': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync brought in a new dispute' : 'A new dispute was added'}${c.where ? ` ${c.where}` : ''}.`,
  'Updated payment': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync updated this payment' : 'This payment was updated'}${c.changes ? ` — ${c.changes}` : ''}.`,
  'Updated refund': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync updated this refund' : 'This refund was updated'}${c.changes ? ` — ${c.changes}` : ''}.`,
  'Updated dispute': (c) => `${fromSync(c) ? 'The Stripe/PayPal sync updated this dispute' : 'This dispute was updated'}${c.changes ? ` — ${c.changes}` : ''}.`,
  'Updated retention': (c) => `Auto Retention set the retention amount${c.where ? ` ${c.where}` : ''}${c.changes ? ` — ${c.changes}` : ''}.`,
  'Cleared retention': (c) => `Auto Retention cleared the retention amount${c.where ? ` ${c.where}` : ''}.`,
  'Updated payment website': (c) => `The sync changed which website this payment belongs to${c.changes ? ` — ${c.changes}` : ''}.`,
  'Created payment': (c) => `A payment was added by hand${c.where ? ` ${c.where}` : ''}.`,
  'Filled deal owner': (c) => `The system filled in the HubSpot deal owner${c.where ? ` ${c.where}` : ''}${c.changes ? ` — ${c.changes}` : ''}.`,
  // The direct-edit audit triggers. Their own label is clear, but the sentence has to keep
  // the part that matters: this did not come through the app. "Changed By" names the writer.
  'Edited directly in the database': (c) =>
    `Changed outside the app, by a script or a direct database connection${c.changes ? ` — ${c.changes}` : ''}${c.where ? ` ${c.where}` : ''}.`,
  'Added directly in the database': (c) =>
    `A row was added outside the app, by a script or a direct database connection${c.where ? ` ${c.where}` : ''}.`,
  'Deleted directly in the database': (c) =>
    `A row was deleted outside the app, by a script or a direct database connection${c.where ? ` ${c.where}` : ''}.`,
};


/** The plain-English line for a log row. A sentence the writer already stored always wins;
 *  everything else is built here from the action and the values that moved — which is why
 *  every one of the rows already in the table gets a note with no backfill and no writer
 *  change. ⚠ The search box filters server-side over `action` and the raw `details`, so a
 *  generated sentence's own wording is not searchable; its website/email/action text is. */
function noteFor(log: ActivityLog, parts: LogParts): string {
  if (parts.prose.length) return parts.prose.join(' ');
  if (parts.deals.length) return dealsNote(log, parts);
  const ctx: NoteCtx = { log, where: whereClause(parts), changes: changeSummary(parts) };
  const tpl = NOTE_BY_ACTION[log.action];
  return tpl ? tpl(ctx) : genericNote(ctx);
}


const DEBOUNCE_MS = 350;

/* ─────────────────────────── component ─────────────────────────── */

type StatusFilter = 'all' | 'ok' | 'fail';

const ApplicationLogsPage: React.FC = () => {
  const [data, setData] = useState<ActivityLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [section, setSection] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [userEmail, setUserEmail] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Hard-delete: the id of the row currently being deleted (disables its button).
  // [FAST DELETE 2026-09-09, user] Ids with a DELETE in flight. A ref, not state: the row is
  // removed from the list the instant it is clicked (see handleDelete), so nothing has to
  // re-render when this changes — it only stops a double-fire for the SAME id.
  const deletingIds = useRef<Set<number>>(new Set());


  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  // Any filter change snaps back to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, section, status, userEmail, from, to, pageSize]);

  // [SMOOTH-DATES 2026-10-12, user] "When I select the dates it jumps": a filter / date / page change swapped the
  // table (up to 50 rows of ~52px) for an 8-row skeleton (~29px rows) and grew back when the data landed. The user's
  // rule of 2026-07-22 stands — the skeleton for the new data, never the old rows lingering — but the skeleton now
  // HOLDS the height of what it replaces (filled with as many rows as are on screen), and the new rows rise in as
  // they land. The hold never happens on the first load; neither happens on a silent live reload. [FLUID-5 2026-10-12]
  // The rise now runs on the first load (a tab visit) too, over every row on screen.
  const contentRef = useRef<HTMLDivElement>(null);
  const [hold, setHold] = useState<{ h: number; rows: number } | null>(null);
  const [landing, setLanding] = useState(0);
  const landRef = useRef<HTMLDivElement>(null);
  useRowsLand(landRef, landing > 0 ? landing : null); // [FLUID-5 2026-10-12]

  // [ACCEPT-REFRESH 2026-09-17, user] `silent` = a live reload after a write made elsewhere in the
  // app (no skeleton). The request id drops a response that a newer load has superseded, so a
  // silent reload can never paint over a filter change made while it was in flight.
  const loadRequestIdRef = useRef(0);
  const load = useCallback(async (silent = false) => {
    const reqId = ++loadRequestIdRef.current;
    if (!silent) {
      // [SMOOTH-DATES 2026-10-12] Measure what is on screen BEFORE the skeleton replaces it.
      const el = contentRef.current;
      if (el && el.offsetHeight > 0) {
        const h = el.offsetHeight;
        const top = pageRect(el).top; // [APP-FIT 2026-10-26] page px, like offsetHeight and the skeleton rows
        const vh = pageViewport().height;
        const seen = top < vh ? Math.min(h, vh - top) : h;
        setHold({ h, rows: Math.max(1, Math.min(60, Math.floor((seen - SK_HEAD_H) / SK_ROW_H))) });
      } else {
        setHold(null);
      }
      setLoading(true);
    }
    setError(null);
    try {
      const res = await fetchActivityLogs({ page, pageSize, section, search, success: status, userEmail, from, to });
      if (reqId !== loadRequestIdRef.current) return;
      setData(res);
      if (!silent) setLanding((n) => n + 1); // [SMOOTH-DATES 2026-10-12] [FLUID-5 2026-10-12] the first load too
    } catch (e: any) {
      if (reqId !== loadRequestIdRef.current) return;
      // [LOAD-STATES 2026-09-29, user] A live (silent) reload that fails keeps the rows on screen.
      if (!silent) setError(e?.response?.data?.error || 'Failed to load activity logs.');
    } finally {
      if (reqId === loadRequestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, section, search, status, userEmail, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  // [ACCEPT-REFRESH 2026-09-17, user] The notification bell's Accept / Cancel fire this (see
  // services/realtime.ts `activityLogs`), so the new entry appears without a page reload. The
  // server holds this read until that request's log row is written (routes/activityLogs.ts).
  useRealtime(['activityLogs'], () => load(true));

  const logs = data?.logs ?? [];

  // [COL-RESIZE-ALL 2026-09-29, user] See LOG_COLS. Off on phones; no tooltips on this page.
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const colResize = useColumnResize({
    storageKey: `dt-application-log-colw:${user?.id ?? 'anon'}`,
    enabled: !isMobile,
    columnKeys: LOG_COL_KEYS,
    headerPadX: LOG_PAD_X,
    refresh: logs,
    hoverTitles: false,
  });
  // [SEARCH-HIGHLIGHT 2026-09-29, user] Amber highlight of the searched text in the log rows.
  useSearchHighlight(colResize.tableElRef, search, loading ? null : logs);
  // A dragged column's cell content sits in the clamp box ($deep: its nested one-line blocks, like
  // date over time and name over email, also get the ellipsis). Untouched columns are unchanged.
  const logCell = (key: string, node: React.ReactNode, center?: boolean): React.ReactNode => {
    const v = colResize.cut?.[key];
    return v
      ? <CutCell $deep data-dt-cut="" style={cutCellStyle(v, LOG_PAD_X, center ? 'center' : undefined)}>{node}</CutCell>
      : node;
  };
  const sections = data?.sections ?? [];
  const users = data?.users ?? [];

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.email, label: u.name ? `${u.name} (${u.email})` : u.email })),
    [users]
  );

  const statusChips: { key: StatusFilter; label: string; danger?: boolean }[] = [
    { key: 'all', label: 'All results' },
    { key: 'ok', label: 'Successful' },
    { key: 'fail', label: 'Failed', danger: true },
  ];

  const hasFilters = !!(search || section !== 'all' || status !== 'all' || userEmail || from || to);
  // [SMOOTH-DATES 2026-10-12] The Clear chip's own width, read just before it slides (fonts are in by then).
  const clearChipRef = useRef<HTMLButtonElement>(null);
  const [clearW, setClearW] = useState(0);
  useLayoutEffect(() => {
    const w = clearChipRef.current?.offsetWidth || 0;
    if (w && w !== clearW) setClearW(w);
  }, [hasFilters, clearW]);
  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setSection('all');
    setStatus('all');
    setUserEmail('');
    setFrom('');
    setTo('');
  };

  // HARD delete a single audit row — permanently removed from the DB (no trash/restore,
  // and no confirmation: one click on the row's trash button deletes it immediately).
  //
  // [FAST DELETE 2026-09-09, user] "the delete button is not fast". Measured before changing
  // anything: the server does exactly ONE round trip to RDS for this request — the middleware
  // chain skips it (activityLogger explicitly, responseCache is pass-through on writes, the
  // auth lookup is cached for 30s, sectionGuard's only await is the Performance branch) — and
  // that round trip is 242 ms from the production box, of which the DELETE itself is ~2 ms.
  // The rest is the Ohio↔India network; nothing server-side can make it faster without moving
  // the database. What made it FEEL slow was this handler:
  //   1. the row only vanished AFTER the response, so every click did nothing visible for the
  //      whole round trip; and
  //   2. `if (deletingId !== null) return` silently DROPPED every click on any other row while
  //      one delete was in flight — while those buttons still looked enabled. Clearing a run of
  //      noise rows meant click, wait, click again, with some clicks lost.
  // Now the row is removed the instant it is clicked and the request goes out behind it;
  // several rows can be in flight at once; and if the server refuses, the row is put back
  // where it was and the error is shown. The row vanishing IS the feedback, so the per-row
  // success toast is gone too — clearing twenty rows must not raise twenty toasts.
  const handleDelete = async (id: number) => {
    if (deletingIds.current.has(id)) return;
    deletingIds.current.add(id);

    // Snapshot the row and where it sat, so a failed delete can restore it in place.
    const at = logs.findIndex((l) => l.id === id);
    const row = at >= 0 ? logs[at] : null;

    // Optimistic: gone now, total kept honest now.
    setData((prev) =>
      prev
        ? {
            ...prev,
            logs: prev.logs.filter((l) => l.id !== id),
            pagination: { ...prev.pagination, totalRows: Math.max(0, prev.pagination.totalRows - 1) },
          }
        : prev
    );

    try {
      await deleteActivityLog(id);
    } catch (e: any) {
      // 404 = the row was already gone (the hygiene sweep collapses repeats on a schedule, so
      // a stale page can show a row the server has since removed). That is the outcome the
      // click wanted — leave it gone, say nothing.
      if (e?.response?.status === 404) return;
      // Anything else: put it back exactly where it was (bounded, in case the list shrank).
      if (row) {
        setData((prev) => {
          if (!prev || prev.logs.some((l) => l.id === id)) return prev;
          const next = prev.logs.slice();
          next.splice(Math.min(at, next.length), 0, row);
          return {
            ...prev,
            logs: next,
            pagination: { ...prev.pagination, totalRows: prev.pagination.totalRows + 1 },
          };
        });
      }
      alertDialog({
        title: 'Delete failed',
        message: e?.response?.data?.error || 'Failed to delete the log entry.',
        destructive: true,
      });
    } finally {
      deletingIds.current.delete(id);
    }
  };

  return (
    <Page>
      <HeaderRow>
        <TitleBlock>
          <Title>Application Logs</Title>
          <Sub>
            Every change made by a signed-in user — who made it (Google account), what they changed,
            which section, and exactly when. Blocked or failed attempts are recorded too, so a wrong
            change can always be traced back to its author.
          </Sub>
        </TitleBlock>
      </HeaderRow>

      <Toolbar>
        <SearchWrap>
          <i className="fas fa-search" />
          <Search
            placeholder="Search by person, action, section, value…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </SearchWrap>
        <FieldWrap>
          <i className="fas fa-user" />
          {/* No title — the select already reads "All people" / the chosen name. */}
          <GlassSelect liquid deferChange /* [FLUID-8 2026-10-12] instant pick */ value={userEmail} onChange={(e) => setUserEmail(e.target.value)} aria-label="Filter by person">
            <option value="">All people</option>
            {userOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </GlassSelect>
        </FieldWrap>
        <FieldWrap>
          From
          {/* [DATE-STYLE 2026-09-29, user] The app's own calendar ("Sep 29, 2026") instead of the
              browser's mm/dd/yyyy box; × clears it (no date = no limit), as emptying the box did. */}
          <SingleDateCalendar liquid value={from} max={to || null} onChange={setFrom} />
          {/* [SMOOTH-DATES 2026-10-12] Always rendered; it slides open / shut (see ClearDate). */}
          <ClearDate type="button" $show={!!from} tabIndex={from ? 0 : -1} aria-hidden={!from} aria-label="Clear the From date" onClick={() => setFrom('')}>×</ClearDate>
        </FieldWrap>
        <FieldWrap>
          To
          <SingleDateCalendar liquid value={to} min={from || null} onChange={setTo} />
          <ClearDate type="button" $show={!!to} tabIndex={to ? 0 : -1} aria-hidden={!to} aria-label="Clear the To date" onClick={() => setTo('')}>×</ClearDate>
        </FieldWrap>
      </Toolbar>

      <ChipsRow>
        {/* [FLUID-8 2026-10-12, user] InstantPick: a chip lights up on the mouse PRESS; the reload (held-height skeleton,
            then the rows) follows as a transition — it used to wait for the release and a whole-page render. */}
        <InstantPick value={section} onPick={setSection}>
          {(shownSection, press) => (
            <Chips>
              <Chip $active={shownSection === 'all'} {...press('all')}>
                All sections
              </Chip>
              {sections.map((s) => (
                <Chip key={s} $active={shownSection === s} {...press(s)}>
                  {s}
                </Chip>
              ))}
            </Chips>
          )}
        </InstantPick>
        <Chips>
          <InstantPick value={status} onPick={setStatus}>
            {(shownStatus, press) => statusChips.map((c) => (
              <Chip key={c.key} $active={shownStatus === c.key} $danger={c.danger} {...press(c.key)}>
                {c.label}
              </Chip>
            ))}
          </InstantPick>
          {/* [SMOOTH-DATES 2026-10-12] Always rendered; it slides in / out (see ChipSlot). */}
          <ChipSlot $show={hasFilters} $w={clearW} aria-hidden={!hasFilters}>
            <Chip ref={clearChipRef} $active={false} onClick={clearFilters} tabIndex={hasFilters ? 0 : -1}>
              <i className="fas fa-xmark" style={{ marginRight: 4 }} /> Clear
            </Chip>
          </ChipSlot>
        </Chips>
      </ChipsRow>

      {/* [SMOOTH-DATES 2026-10-12] Holds the height of what the skeleton replaced, while it shows. */}
      <div ref={contentRef} style={loading && hold ? { minHeight: hold.h } : undefined}>
      {loading ? (
        <Card>
          <TableSkeleton
            rows={hold ? hold.rows : 8}
            columns={[
              { label: 'Time changed' },
              { label: 'Name' },
              { label: 'Section', align: 'center' },
              { label: 'Action' },
              { label: 'Changes Made' },
              { label: 'Note' },
              { label: 'Status', align: 'center' },
              { label: 'Delete', align: 'center' },
            ]}
          />
        </Card>
      ) : error ? (
        // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
        <LoadFailed what="activity logs" onRetry={() => load()} />
      ) : logs.length === 0 ? (
        hasFilters
          ? <EmptyState icon="search" title="No activity matches your filters" hint="Widen the dates or clear the filters to see more." />
          : <EmptyState title="No activity recorded yet" hint="Every change made in the dashboard shows up here." />
      ) : (
        <Card ref={landRef}>
          <TableScroll>
            <Table ref={colResize.tableElRef} style={colResize.tableStyle}>
              <thead>
                <tr>
                  {/* [COL-RESIZE-ALL 2026-09-29] Th is already position:sticky, which anchors the handle. */}
                  {LOG_COLS.map((c) => (
                    <Th key={c.key} $center={c.center} style={colResize.thStyle(c.key)}>
                      {colResize.cut?.[c.key]
                        ? (
                          <span style={{ display: 'inline-flex', verticalAlign: 'middle', ...colResize.labelStyle(c.key) }}>
                            <CutLabel data-dt-cut="">{c.label}</CutLabel>
                          </span>
                        )
                        : c.label}
                      {colResize.active && <ResizeHandle {...colResize.handleProps(c.key, c.label)} />}
                    </Th>
                  ))}
                  <Th $center>Delete</Th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log: ActivityLog) => {
                  const when = fmtWhen(log.created_at);
                  // [LOG-NOTE 2026-09-18, user] One pass over the row's details feeds both
                  // columns: the record + the values that moved on the left, the sentence
                  // on the right, and the copy button gets all of it as plain text.
                  const parts = splitDetails(log.details);
                  const deals = parts.deals.map((d, i) => dealLines(d, i, parts.deals.length));
                  const note = noteFor(log, parts);
                  const hasChanges = parts.record.length > 0 || parts.changes.length > 0 || deals.length > 0;
                  // The auto-audit trigger tags its rows with this path. These are
                  // automatic recomputes of derived columns (Total Amount, Mapped
                  // Status, Sale/Upsell totals) — a consequence of a human edit or a
                  // sync, never a manual mapping decision. Flag them so the log is clear.
                  const isAutoColumn = log.path === 'system/auto-column';
                  // [LOG-COVERAGE 2026-09-17, user] Written by the database's direct-edit audit: a
                  // change made outside the app and the sync (a script, a database client).
                  const isDirectDb = log.path === 'system/direct-db';
                  return (
                    <Tr key={log.id}>
                      <Td $top>
                        {logCell('time', (
                          <WhenCell>
                            <div className="date">{when.date}</div>
                            <div className="time">{when.time}</div>
                          </WhenCell>
                        ))}
                      </Td>
                      <Td $top>
                        {/* [NO TOOLTIPS 2026-09-09, user] "remove all the tooltip from the
                            application log, no need to add there". The person's email is already
                            printed on the line below their name, so the hover only repeated it. */}
                        {logCell('name', (
                          <PersonCell>
                            <Avatar>{initialOf(log.user_name, log.user_email)}</Avatar>
                            <div style={{ minWidth: 0 }}>
                              <PersonName>{log.user_name || log.user_email || 'Unknown'}</PersonName>
                              {log.user_email && <PersonEmail>{log.user_email}</PersonEmail>}
                            </div>
                          </PersonCell>
                        ))}
                      </Td>
                      <Td $center $top>
                        {logCell('section', <SectionChip>{log.section}</SectionChip>, true)}
                      </Td>
                      <Td $top>
                        {logCell('action', (
                          <ActionWrap>
                            <ActionText>{log.action}</ActionText>
                            {isAutoColumn && (
                              <AutoNote>Auto-computed by the system after the edit/sync that changed these values — not entered by hand.</AutoNote>
                            )}
                            {isDirectDb && (
                              <AutoNote>Changed outside the app — by a script or a direct database connection. "Changed By" names it.</AutoNote>
                            )}
                          </ActionWrap>
                        ))}
                      </Td>
                      <Td $top>
                        {logCell('changes', !hasChanges ? (
                          <Dash>—</Dash>
                        ) : (
                          <>
                            {/* No title on KV: the tooltip repeated the chip's own visible text
                                word for word (the "Refund Amount: 300 → 215.46" hover). The
                                auto-column caption that used to sit here now lives in Note. */}
                            {parts.record.length > 0 && (
                              <Details>
                                {parts.record.map((p, i) => (
                                  <KV key={`r-${p.k}-${i}`}>
                                    <b>{prettyKey(p.k)}:</b>
                                    <span>{p.v}</span>
                                  </KV>
                                ))}
                              </Details>
                            )}
                            {parts.changes.length > 0 && (
                              <Details $sep={parts.record.length > 0}>
                                {parts.changes.map((p, i) => (
                                  <KV key={`c-${p.k}-${i}`}>
                                    <b>{prettyKey(p.k)}:</b>
                                    <span>{p.v}</span>
                                  </KV>
                                ))}
                              </Details>
                            )}
                            {deals.length > 0 && (
                              <Details $sep={parts.record.length > 0 || parts.changes.length > 0}>
                                {deals.map((lines, di) =>
                                  lines.map((l, li) => (
                                    <KV key={`d-${di}-${li}`}>
                                      <b>{l.label}:</b>
                                      <span>{l.v}</span>
                                    </KV>
                                  ))
                                )}
                              </Details>
                            )}
                          </>
                        ))}
                      </Td>
                      <Td $top>
                        {logCell('note', note ? <NoteCell>{note}</NoteCell> : <Dash>—</Dash>)}
                      </Td>
                      <Td $center $top>
                        {logCell('status', (
                          <StatusBadge $ok={log.success}>
                            <i className={`fas ${log.success ? 'fa-check' : 'fa-triangle-exclamation'}`} />
                            {log.success ? 'Successful' : 'Failed'}
                          </StatusBadge>
                        ), true)}
                      </Td>
                      <Td $center $top>
                        <DeleteBtn
                          type="button"
                          aria-label="Delete log entry"
                          onClick={() => handleDelete(log.id)}
                        >
                          <i className="fas fa-trash-can" />
                        </DeleteBtn>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
          {data && (
            <Pagination
              pagination={data.pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </Card>
      )}
      </div>
    </Page>
  );
};

export default ApplicationLogsPage;
